import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { companyInfo } from "src/data/general";
import { backgroundImg } from "src/assets/assetsExport";
import { GuideSection } from "./GuideSection";

const depositFees = [
  {
    label: "Annual rent up to £50,000",
    value: "Equivalent to five weeks' rent",
  },
  {
    label: "Annual rent exceeding £50,000",
    value: "Equivalent to six weeks' rent",
  },
  {
    label: "Deposit for a short let tenancy",
    value:
      "£700 or equivalent to one weeks' rent (whichever is greater)",
  },
];

const otherFees = [
  {
    label:
      "Contract variation, novation, amendment or change of occupant at the tenant's request within an existing tenancy",
    value: "£50 (or reasonable costs if higher)",
  },
  {
    label:
      "Default fee of interest on late rent if rent is more than 14 days overdue",
    value: "3% above Bank of England base rate",
  },
  {
    label: "Default fee for lost keys or other respective security devices",
    value: "Actual cost of replacement",
  },
];

const TenantFeesPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Tenant Fees | Letting Engine</title>
        <meta
          name="description"
          content="Transparent information about tenant fees, holding deposits, referencing, and tenancy deposit protection with Letting Engine."
        />
      </Helmet>

      <section className="min-h-[60vh] pt-40 pb-16 w-full relative flex items-center justify-center">
        <img
          src={backgroundImg.livingRoom3}
          alt=""
          className="absolute inset-0 w-full h-full object-cover pointer-events-none z-0"
          aria-hidden
        />
        <div className="absolute inset-0 bg-black/40 z-0" />
        <div className="relative z-10 text-center max-w-3xl px-6">
          <h1 className="font-teko leading-none mb-6 tracking-[-2px] text-6xl text-white">
            Tenant Fees
          </h1>
          <p className="font-manrope text-base leading-relaxed text-white/90">
            Clear information about the costs associated with renting through
            Letting Engine. We confirm all applicable fees in writing before you
            proceed.
          </p>
        </div>
      </section>

      <GuideSection eyebrow="Overview" title="Fees">
        <div className="max-w-3xl space-y-10">
          <div>
            <h3 className="font-teko text-3xl mb-6">Deposit</h3>
            <ul className="space-y-4 font-manrope text-base text-text-slate leading-relaxed">
              {depositFees.map((fee) => (
                <li key={fee.label}>
                  <span className="font-medium">{fee.label}:</span> {fee.value}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-teko text-3xl mb-6">Other fees</h3>
            <ul className="space-y-4 font-manrope text-base text-text-slate leading-relaxed">
              {otherFees.map((fee) => (
                <li key={fee.label}>
                  <span className="font-medium">{fee.label}:</span> {fee.value}
                </li>
              ))}
            </ul>
          </div>
          <p className="font-manrope text-base text-text-slate leading-relaxed">
            All fees unless otherwise stated are inclusive of VAT
          </p>
        </div>
      </GuideSection>

      <GuideSection
        eyebrow="Deposit Protection"
        title="DPS & Your Rights"
        className="bg-bg-porcelain"
      >
        <div className="max-w-3xl space-y-6 font-manrope text-base text-text-slate leading-relaxed">
          <p>
            Your tenancy deposit is protected with the{" "}
            <a
              href="https://www.depositprotection.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-gold underline"
            >
              Deposit Protection Service (DPS)
            </a>
            . At the end of your tenancy, your deposit will be returned subject
            to the terms of your agreement and any agreed deductions for
            damage or unpaid rent.
          </p>
          <p>
            You will receive a copy of the{" "}
            <a
              href="https://www.gov.uk/government/publications/how-to-rent"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-gold underline"
            >
              How to Rent guide
            </a>{" "}
            and prescribed information about your deposit protection before or
            at the start of your tenancy.
          </p>
        </div>
      </GuideSection>

      <GuideSection eyebrow="Questions" title="Get a Fee Breakdown">
        <div className="max-w-2xl">
          <p className="font-manrope text-base text-text-slate leading-relaxed mb-8">
            Fees depend on the property and tenancy terms. For a personalised
            quote before you apply, contact our team — we are happy to explain
            every cost with no obligation.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/contact-us"
              className="px-8 py-3 bg-text-obsidian text-white font-teko text-xl uppercase tracking-wide hover:bg-brand-yellow hover:text-black transition-all"
            >
              Contact Us
            </Link>
            <Link
              to="/guides/tenants"
              className="px-8 py-3 border border-solid border-text-obsidian font-teko text-xl uppercase tracking-wide hover:border-brand-yellow transition-all"
            >
              Back to Tenant Guides
            </Link>
          </div>
          <p className="font-manrope text-sm text-text-slate mt-8">
            <a
              href={`mailto:${companyInfo.email}`}
              className="text-brand-gold underline"
            >
              {companyInfo.email}
            </a>
            {" · "}
            <a
              href={`tel:${companyInfo.phone.replace(/[^\d+]/g, "")}`}
              className="text-brand-gold underline"
            >
              {companyInfo.phone}
            </a>
          </p>
        </div>
      </GuideSection>
    </>
  );
};

export default TenantFeesPage;
